/**
 * The animated background effects, assembled.
 *
 * The chunks themselves live in `bgEffects/` one file per KIND, because a
 * single file holding every effect crossed a thousand lines and nobody could
 * find anything in it:
 *   - `fields.ts`    continuous colour: blobs, gradients, beams, clouds
 *   - `patterns.ts`  a repeating unit: grids, lattices, dot fields, textures
 *   - `particles.ts` many small discrete pieces: stars, motes, drops, sparks
 *   - `scenes.ts`    a composed picture with a subject: horizon, sun, dial
 *   - `cosmos.ts`    a body or a depth in space: tunnel, galaxy, orbit, planet
 *   - `screens.ts`   a display surface or instrument readout: tube, trace, beat
 *   - `optics.ts`    light through a lens or off a film: flare, leak, sheen
 *   - `worksite.ts`  a building site: structure, warning marks, sparks
 *   - `freight.ts`   goods on the move: yard, belt, road, aisle, label
 *   - `flooring.ts`  a laid surface: parquet, decking, poured stone
 * A new effect goes in whichever file matches, gets a row in `BG_EFFECT_PART_CSS`
 * below and a row in `BG_EFFECT_PARTS` (`bgEffectParts.ts`, the DOM each effect
 * draws - split out of here because the two maps grow together but are read by
 * different callers). Nothing else in the repo needs to change - the picker, the
 * showIf guards, the card stack and the scroll gallery all derive from the
 * catalog in `../bgEffects.ts`.
 *
 * Why the CSS is split per effect at all: `BgEffectLayer` injects a `<style>`
 * tag per instance, so a page with ten effect sections used to carry ten copies
 * of the WHOLE stylesheet. The layer now injects only the base rules plus the
 * one chunk it needs, while `BG_EFFECT_CSS` (base + every chunk) stays exported
 * for the static-export pipeline, which writes the stylesheet once.
 *
 * House rules for a chunk, wherever it lives:
 *   - Pure CSS. No JS, no measurement, SSR-safe.
 *   - Colours come from `--wa-bge-c1/c2/c3`, timing from `--wa-bge-dur`, blur
 *     from `--wa-bge-blur` (directly or through a `calc()` factor).
 *   - Animate only real elements, never pseudo-elements: the "Animate off"
 *     switch and `prefers-reduced-motion` pause through `.wa-bge-static *`
 *     and `.wa-bge *`, and neither selector reaches a `::before`.
 *   - A group class must NEVER be spelled `wa-bge-<id>`. The wrapper carries
 *     that class and would inherit the child's rules, silently.
 *   - Give staggered pieces NEGATIVE delays so the paused (static) frame is
 *     still a composition and not everything stacked on its start pose.
 *   - A tiled layer that travels must move by an exact multiple of its
 *     `background-size`, and needs at least one full tile of margin beyond
 *     each edge, or its own edge slides into frame.
 *   - Size a shape off the SHORT side (`inset:0; margin:auto` plus a
 *     height-driven `aspect-ratio`) when it must stay whole in a short, wide
 *     section. A percentage width measures the wrong side.
 * `bgEffectCss.test.ts` enforces the ones a machine can check.
 */
import type { BgEffectId } from "../bgEffects.js";
import {
  BLOB_CSS,
  CAUSTICS_CSS,
  CONIC_CSS,
  CURTAIN_CSS,
  EDGE_CSS,
  FOG_CSS,
  GRADIENT_CSS,
  INK_CSS,
  KALEIDO_CSS,
  MARBLE_CSS,
  NEBULA_CSS,
  PRISM_CSS,
  PULSE_CSS,
  RAYS_CSS,
  SHEEN_CSS,
  SMOKE_CSS,
  LAVA_CSS,
  PLASMA_CSS,
  SPOTLIGHT_CSS,
  VORTEX_CSS,
  WAVES_CSS,
} from "./bgEffects/fields.js";
import { ATOM_CSS, GALAXY_CSS, SATURN_CSS, WORMHOLE_CSS } from "./bgEffects/cosmos.js";
import { CRT_CSS, HEARTBEAT_CSS, HOLOGRAM_CSS, MOIRE_CSS, WAVEFORM_CSS } from "./bgEffects/screens.js";
import { BORDER_CSS, FLARE_CSS, IRIDESCENT_CSS, LEAK_CSS, RIBBON_CSS } from "./bgEffects/optics.js";
import { CRANE_CSS, HAZARD_CSS, PIPES_CSS, REBAR_CSS, SCAFFOLD_CSS, TRUSS_CSS, WELD_CSS } from "./bgEffects/worksite.js";
import { BARCODE_CSS, CHEVRON_CSS, CONTAINERS_CSS, CONVEYOR_CSS, HIGHWAY_CSS, PALLETS_CSS } from "./bgEffects/freight.js";
import { BLOCKWOOD_CSS, DECKING_CSS, PARQUET_CSS, TERRAZZO_CSS } from "./bgEffects/flooring.js";
import {
  BLUEPRINT_CSS,
  CHECKERS_CSS,
  CIRCUIT_CSS,
  CONSTELLATION_CSS,
  DITHER_CSS,
  DOTS_CSS,
  DOTWAVE_CSS,
  GRID_CSS,
  GRIDBEAM_CSS,
  HALFTONE_CSS,
  HATCH_CSS,
  HELIX_CSS,
  HEX_CSS,
  ISO_CSS,
  LINES_CSS,
  MATRIX_CSS,
  MOSAIC_CSS,
  NOISE_CSS,
  PLUS_CSS,
  SCAN_CSS,
  SHATTER_CSS,
  TOPO_CSS,
} from "./bgEffects/patterns.js";
import {
  BOKEH_CSS,
  BUBBLES_CSS,
  CONFETTI_CSS,
  CUBES_CSS,
  DROPLETS_CSS,
  DUST_CSS,
  EMBER_CSS,
  FIREFLIES_CSS,
  FIREWORKS_CSS,
  GLINTS_CSS,
  LEAVES_CSS,
  ORBS_CSS,
  RAIN_CSS,
  SNOW_CSS,
  STARS_CSS,
  TRAILS_CSS,
} from "./bgEffects/particles.js";
import {
  ARCS_CSS,
  BARS_CSS,
  BLINDS_CSS,
  GEARS_CSS,
  GLASS_CSS,
  GLITCH_CSS,
  GLOBE_CSS,
  LIGHTNING_CSS,
  LOWPOLY_CSS,
  NEON_CSS,
  RADAR_CSS,
  RIPPLE_CSS,
  SINE_CSS,
  SKYLINE_CSS,
  STRIKE_CSS,
  SUNSET_CSS,
  TERRAIN_CSS,
  WARP_CSS,
} from "./bgEffects/scenes.js";

/**
 * Shared shell plus the registered angle both `conic` and `rays` animate.
 * `@property` is what lets an angle interpolate at all; older Safari simply
 * holds the initial value, which degrades to a static frame.
 *
 * `.wa-bge-fix` is the PINNED variant ("Fixed background"): the effect stops
 * scrolling with its section and stands still on the screen while the content
 * travels over it. Two rules make that work, and both are load-bearing:
 *
 *   - The layer itself becomes `position:fixed`, so its box is the VIEWPORT.
 *     That also means every effect is composed at viewport size rather than at
 *     section size, which is the point: the section becomes a window onto one
 *     full-screen background.
 *   - The wrapper clips it back down with `clip-path`, NOT with `overflow`.
 *     A fixed descendant escapes an ancestor's `overflow:hidden/clip` (the
 *     section's own clip does nothing to it), but `clip-path` is a paint-time
 *     clip that DOES reach it - and, unlike `transform`, `filter` or
 *     `contain:paint`, it does not become the containing block, so the layer
 *     stays anchored to the viewport. Verified in the browser: two identical
 *     pinned sections at different page offsets share one viewport-wide stripe
 *     grid instead of each restarting at its own top edge.
 *
 * `--wa-bge-r` carries the section's corner radius into that clip, because the
 * clip is square by default and would let the effect poke out of a rounded
 * section's corners.
 */
export const BG_EFFECT_BASE_CSS = `
.wa-bge{position:absolute;inset:0;overflow:hidden;z-index:0;pointer-events:none;}
.wa-bge-fix{position:absolute;inset:0;z-index:0;pointer-events:none;clip-path:inset(0 round var(--wa-bge-r,0px));}
.wa-bge-fix>.wa-bge{position:fixed;inset:0;}
@property --wa-bge-angle{syntax:"<angle>";inherits:false;initial-value:0deg;}
`;

/**
 * The two pause switches, and they MUST be emitted after every effect chunk.
 * `.wa-bge-static *` and `.wa-bge *` both weigh one class, exactly as much as a
 * rule like `.wa-bge-b1`, so the cascade is decided by source order alone - and
 * every effect rule sets the `animation:` SHORTHAND, which resets
 * `animation-play-state` back to `running`. Put these first and the "Animate
 * off" switch plus `prefers-reduced-motion` both stop working, silently.
 */
export const BG_EFFECT_PAUSE_CSS = `
.wa-bge-static *{animation-play-state:paused;}
@media (prefers-reduced-motion:reduce){.wa-bge *{animation-play-state:paused;}}
`;

/** Effect id -> the CSS chunk it needs (base rules excluded). */
export const BG_EFFECT_PART_CSS: Record<BgEffectId, string> = {
  aurora: BLOB_CSS,
  mesh: BLOB_CSS,
  conic: CONIC_CSS,
  gradient: GRADIENT_CSS,
  spotlight: SPOTLIGHT_CSS,
  stars: STARS_CSS,
  grid: GRID_CSS,
  waves: WAVES_CSS,
  orbs: ORBS_CSS,
  sheen: SHEEN_CSS,
  pulse: PULSE_CSS,
  nebula: NEBULA_CSS,
  curtain: CURTAIN_CSS,
  bokeh: BOKEH_CSS,
  dust: DUST_CSS,
  rays: RAYS_CSS,
  ripple: RIPPLE_CSS,
  dots: DOTS_CSS,
  lines: LINES_CSS,
  hex: HEX_CSS,
  topo: TOPO_CSS,
  noise: NOISE_CSS,
  blueprint: BLUEPRINT_CSS,
  edge: EDGE_CSS,
  fog: FOG_CSS,
  halftone: HALFTONE_CSS,
  arcs: ARCS_CSS,
  circuit: CIRCUIT_CSS,
  iso: ISO_CSS,
  rain: RAIN_CSS,
  snow: SNOW_CSS,
  glints: GLINTS_CSS,
  scan: SCAN_CSS,
  caustics: CAUSTICS_CSS,
  marble: MARBLE_CSS,
  hatch: HATCH_CSS,
  sine: SINE_CSS,
  lowpoly: LOWPOLY_CSS,
  radar: RADAR_CSS,
  smoke: SMOKE_CSS,
  glass: GLASS_CSS,
  terrain: TERRAIN_CSS,
  prism: PRISM_CSS,
  vortex: VORTEX_CSS,
  warp: WARP_CSS,
  droplets: DROPLETS_CSS,
  constellation: CONSTELLATION_CSS,
  sunset: SUNSET_CSS,
  ink: INK_CSS,
  fireflies: FIREFLIES_CSS,
  ember: EMBER_CSS,
  leaves: LEAVES_CSS,
  mosaic: MOSAIC_CSS,
  plus: PLUS_CSS,
  lightning: LIGHTNING_CSS,
  bars: BARS_CSS,
  glitch: GLITCH_CSS,
  blinds: BLINDS_CSS,
  neon: NEON_CSS,
  skyline: SKYLINE_CSS,
  strike: STRIKE_CSS,
  bubbles: BUBBLES_CSS,
  kaleido: KALEIDO_CSS,
  helix: HELIX_CSS,
  globe: GLOBE_CSS,
  gears: GEARS_CSS,
  checkers: CHECKERS_CSS,
  matrix: MATRIX_CSS,
  confetti: CONFETTI_CSS,
  fireworks: FIREWORKS_CSS,
  plasma: PLASMA_CSS,
  lava: LAVA_CSS,
  moire: MOIRE_CSS,
  shatter: SHATTER_CSS,
  trails: TRAILS_CSS,
  hologram: HOLOGRAM_CSS,
  crt: CRT_CSS,
  heartbeat: HEARTBEAT_CSS,
  wormhole: WORMHOLE_CSS,
  galaxy: GALAXY_CSS,
  atom: ATOM_CSS,
  saturn: SATURN_CSS,
  hazard: HAZARD_CSS,
  rebar: REBAR_CSS,
  scaffold: SCAFFOLD_CSS,
  truss: TRUSS_CSS,
  crane: CRANE_CSS,
  pipes: PIPES_CSS,
  weld: WELD_CSS,
  containers: CONTAINERS_CSS,
  conveyor: CONVEYOR_CSS,
  highway: HIGHWAY_CSS,
  pallets: PALLETS_CSS,
  chevron: CHEVRON_CSS,
  barcode: BARCODE_CSS,
  parquet: PARQUET_CSS,
  blockwood: BLOCKWOOD_CSS,
  decking: DECKING_CSS,
  terrazzo: TERRAZZO_CSS,
  flare: FLARE_CSS,
  leak: LEAK_CSS,
  iridescent: IRIDESCENT_CSS,
  ribbon: RIBBON_CSS,
  frame: BORDER_CSS,
  dither: DITHER_CSS,
  dotwave: DOTWAVE_CSS,
  gridbeam: GRIDBEAM_CSS,
  cubes: CUBES_CSS,
  waveform: WAVEFORM_CSS,
};

/** Effect id -> the child spans it renders. Lives in `bgEffectParts.ts`;
 *  re-exported here so the one import site keeps its path. */
export { BG_EFFECT_PARTS } from "./bgEffectParts.js";

/** Base rules, the one chunk this effect needs, then the pause switches - what
 *  a single instance injects into the page. The order is the contract. */
export function bgEffectCss(effect: BgEffectId): string {
  return BG_EFFECT_BASE_CSS + BG_EFFECT_PART_CSS[effect] + BG_EFFECT_PAUSE_CSS;
}

/**
 * The whole stylesheet, every chunk exactly once, pause switches last. Used by
 * the static-export pipeline, which writes one shared stylesheet instead of
 * per-instance tags.
 */
export const BG_EFFECT_CSS: string =
  BG_EFFECT_BASE_CSS + Array.from(new Set(Object.values(BG_EFFECT_PART_CSS))).join("") + BG_EFFECT_PAUSE_CSS;
